// src/features/marketplace/pages/SearchResults.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { Search, SlidersHorizontal, X, Pin } from 'lucide-react';
import { ListingCard } from '../components/ListingCard';

export const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filters state
  const [filters, setFilters] = useState({
    brand: searchParams.get('brand') || '',
    location: searchParams.get('location') || '',
    minPrice: '',
    maxPrice: '',
    minYear: '',
    maxYear: '',
    fuel: ''
  });

  useEffect(() => {
    setLoading(true);
    
    // Build query
    let q = query(
      collection(db, "marketplace_listings"),
      where("status", "==", "active"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      let results = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Client-side filtering
      if (filters.brand) {
        results = results.filter(l => 
          l.brand?.toLowerCase().includes(filters.brand.toLowerCase()) ||
          l.model?.toLowerCase().includes(filters.brand.toLowerCase()) ||
          l.title?.toLowerCase().includes(filters.brand.toLowerCase())
        );
      }

      if (filters.location) {
        results = results.filter(l => 
          l.location?.name?.toLowerCase().includes(filters.location.toLowerCase())
        );
      }

      if (filters.minPrice) {
        results = results.filter(l => l.price >= parseInt(filters.minPrice));
      }

      if (filters.maxPrice) {
        results = results.filter(l => l.price <= parseInt(filters.maxPrice));
      }

      if (filters.minYear) {
        results = results.filter(l => l.year >= parseInt(filters.minYear));
      }

      if (filters.maxYear) {
        results = results.filter(l => l.year <= parseInt(filters.maxYear));
      }

      if (filters.fuel) {
        results = results.filter(l => l.fuel === filters.fuel);
      }

      // SORTOWANIE: PINNED FIRST
      results.sort((a, b) => {
        // PINNED na górze
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        
        // Potem po dacie (najnowsze)
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      setListings(results);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      location: '',
      minPrice: '',
      maxPrice: '',
      minYear: '',
      maxYear: '',
      fuel: ''
    });
    setSearchParams({});
  };

  const pinnedCount = listings.filter(l => l.isPinned).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-7xl mx-auto flex gap-3">
          <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-xl">
            <Search className="text-gray-400" size={20}/>
            <input
              type="text"
              placeholder="Szukaj marki, modelu..."
              className="flex-1 bg-transparent outline-none text-gray-900"
              value={filters.brand}
              onChange={(e) => handleFilterChange('brand', e.target.value)}
            />
            {filters.brand && (
              <button onClick={() => handleFilterChange('brand', '')} className="text-gray-400 hover:text-gray-600">
                <X size={18}/>
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
              showFilters 
                ? 'bg-emerald-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <SlidersHorizontal size={20}/>
            <span className="hidden md:inline">Filtry</span>
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border-b border-gray-200 px-4 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Cena od</label>
                <input
                  type="number"
                  placeholder="0"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Cena do</label>
                <input
                  type="number"
                  placeholder="1000000"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Rok od</label>
                <input
                  type="number"
                  placeholder="2000"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  value={filters.minYear}
                  onChange={(e) => handleFilterChange('minYear', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Rok do</label>
                <input
                  type="number"
                  placeholder="2024"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  value={filters.maxYear}
                  onChange={(e) => handleFilterChange('maxYear', e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Paliwo</label>
                <select
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  value={filters.fuel}
                  onChange={(e) => handleFilterChange('fuel', e.target.value)}
                >
                  <option value="">Wszystkie</option>
                  <option value="benzyna">Benzyna</option>
                  <option value="diesel">Diesel</option>
                  <option value="hybryda">Hybryda</option>
                  <option value="elektryczny">Elektryczny</option>
                  <option value="lpg">LPG</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Lokalizacja</label>
                <input
                  type="text"
                  placeholder="Miasto..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-3 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Wyczyść filtry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="max-w-7xl mx-auto p-4 pb-32">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">
              Znaleziono: <span className="font-bold text-gray-900">{listings.length}</span> ogłoszeń
            </p>
            {pinnedCount > 0 && (
              <div className="mt-1 flex items-center gap-2 text-xs text-amber-600 font-semibold">
                <Pin size={14} />
                <span>{pinnedCount} {pinnedCount === 1 ? 'promowane' : 'promowanych'}</span>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-gray-500">Ładowanie...</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Search className="mx-auto mb-4 text-gray-300" size={64}/>
            <p className="text-gray-500 font-semibold">Brak wyników</p>
            <p className="text-sm text-gray-400 mt-2">Spróbuj zmienić kryteria wyszukiwania</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map(listing => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};